import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ChevronRight,
  Pencil,
  Plus,
  Tag as TagIcon,
  Trash2,
} from "lucide-react";
import { MAX_EQUIPMENT_TAG_DEPTH } from "@production-hub/shared";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { PageHeader } from "@/components/PageHeader";
import { SearchInput } from "@/components/SearchInput";
import { EquipmentTagDialog } from "@/components/equipment/EquipmentTagDialog";
import { getEquipmentIcon } from "@/components/equipment/IconPicker";
import {
  useDeleteEquipmentTag,
  useEquipmentIdsByTag,
  useEquipmentTagsTree,
  type EquipmentTag,
  type EquipmentTagTreeNode,
} from "@/hooks/useEquipmentTags";
import { cn } from "@/lib/utils";

interface TagStats {
  /** Distinct equipment in the tag and all its sub-tags */
  equipmentCount: number;
  /** Number of sub-tags at any depth */
  descendantCount: number;
}

function collectStats(
  nodes: EquipmentTagTreeNode[],
  equipmentIdsByTag: Record<string, string[]>,
  result: Map<string, TagStats>,
): Set<string>[] {
  return nodes.map((node) => {
    const equipment = new Set(equipmentIdsByTag[node.id] ?? []);
    const childSets = collectStats(node.children, equipmentIdsByTag, result);
    let descendantCount = node.children.length;
    for (const [index, child] of node.children.entries()) {
      for (const id of childSets[index]) equipment.add(id);
      descendantCount += result.get(child.id)?.descendantCount ?? 0;
    }
    result.set(node.id, { equipmentCount: equipment.size, descendantCount });
    return equipment;
  });
}

/** Keeps nodes that match the search, plus their ancestors. */
function filterTree(
  nodes: EquipmentTagTreeNode[],
  search: string,
): EquipmentTagTreeNode[] {
  if (!search) return nodes;
  const lower = search.toLowerCase();
  return nodes.flatMap((node) => {
    const children = filterTree(node.children, search);
    if (node.name.toLowerCase().includes(lower) || children.length > 0) {
      return [{ ...node, children }];
    }
    return [];
  });
}

function collectIdsWithChildren(nodes: EquipmentTagTreeNode[]): string[] {
  return nodes.flatMap((node) =>
    node.children.length > 0
      ? [node.id, ...collectIdsWithChildren(node.children)]
      : [],
  );
}

const EquipmentTagsPage = () => {
  const { data: tree = [], isLoading } = useEquipmentTagsTree();
  const { data: equipmentIdsByTag } = useEquipmentIdsByTag();
  const deleteTag = useDeleteEquipmentTag();

  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [dialog, setDialog] = useState<{
    open: boolean;
    tag: EquipmentTag | null;
    parentId: string | null;
  }>({ open: false, tag: null, parentId: null });
  const [tagToDelete, setTagToDelete] = useState<EquipmentTag | null>(null);

  const stats = useMemo(() => {
    const result = new Map<string, TagStats>();
    collectStats(tree, equipmentIdsByTag, result);
    return result;
  }, [tree, equipmentIdsByTag]);

  const visibleTree = useMemo(() => filterTree(tree, search), [tree, search]);
  const isSearching = search.length > 0;

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const openCreate = (parentId: string | null = null) =>
    setDialog({ open: true, tag: null, parentId });
  const openEdit = (tag: EquipmentTag) =>
    setDialog({ open: true, tag, parentId: null });

  const deleteStats = tagToDelete ? stats.get(tagToDelete.id) : undefined;

  const renderNode = (node: EquipmentTagTreeNode, depth: number) => {
    const hasChildren = node.children.length > 0;
    // While searching, show every match together with its ancestors
    const isOpen = isSearching || expanded.has(node.id);
    const Icon = getEquipmentIcon(node.icon) ?? TagIcon;
    const nodeStats = stats.get(node.id);
    const level = node.depth + 1;
    const canAddSubTag = level < MAX_EQUIPMENT_TAG_DEPTH;

    return (
      <li key={node.id}>
        <div
          className="group flex items-center gap-2 border-b py-1.5 pr-2 hover:bg-muted/40"
          style={{ paddingLeft: `${depth * 24 + 8}px` }}
          data-testid={`equipment-tag-manage-row-${node.id}`}
        >
          {hasChildren ? (
            <button
              type="button"
              aria-label={isOpen ? "Collapse" : "Expand"}
              onClick={() => toggle(node.id)}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted"
            >
              <ChevronRight
                className={cn(
                  "h-4 w-4 transition-transform",
                  isOpen && "rotate-90",
                )}
              />
            </button>
          ) : (
            <span className="w-6 shrink-0" />
          )}
          <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
          <Link
            to={`/equipment/tag/${node.id}`}
            className="min-w-0 truncate font-medium hover:underline"
          >
            {node.name}
          </Link>
          <span className="shrink-0 text-xs text-muted-foreground">
            {nodeStats?.equipmentCount ?? 0}{" "}
            {nodeStats?.equipmentCount === 1 ? "item" : "items"}
          </span>
          {level > MAX_EQUIPMENT_TAG_DEPTH && (
            <span
              className="flex shrink-0 items-center gap-1 text-xs text-amber-600 dark:text-amber-400"
              title={`Tags may be nested at most ${MAX_EQUIPMENT_TAG_DEPTH} levels. Move this tag to a higher level.`}
              data-testid={`equipment-tag-too-deep-${node.id}`}
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              Level {level}: too deep
            </span>
          )}
          <div className="ml-auto flex shrink-0 gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              title={
                canAddSubTag
                  ? "Add sub-tag"
                  : `Maximum of ${MAX_EQUIPMENT_TAG_DEPTH} levels reached`
              }
              disabled={!canAddSubTag}
              onClick={() => openCreate(node.id)}
              data-testid={`add-equipment-sub-tag-${node.id}`}
            >
              <Plus className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              title="Edit tag"
              onClick={() => openEdit(node)}
              data-testid={`edit-equipment-tag-${node.id}`}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-destructive hover:text-destructive"
              title="Delete tag"
              onClick={() => setTagToDelete(node)}
              data-testid={`delete-equipment-tag-${node.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {hasChildren && isOpen && (
          <ul>{node.children.map((child) => renderNode(child, depth + 1))}</ul>
        )}
      </li>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Equipment Tags">
        <Button
          offlineEdit
          onClick={() => openCreate()}
          data-testid="add-equipment-tag"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Tag
        </Button>
      </PageHeader>

      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/equipment">Equipment</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Tags</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex-1">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search tags..."
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setExpanded(new Set(collectIdsWithChildren(tree)))}
          >
            Expand all
          </Button>
          <Button variant="outline" onClick={() => setExpanded(new Set())}>
            Collapse all
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border">
        {isLoading ? (
          <p className="p-8 text-center text-muted-foreground">
            Loading tags...
          </p>
        ) : visibleTree.length === 0 ? (
          <p className="p-8 text-center text-muted-foreground">
            {isSearching ? "No tags match your search" : "No tags yet"}
          </p>
        ) : (
          <ul className="-mb-px">
            {visibleTree.map((node) => renderNode(node, 0))}
          </ul>
        )}
      </div>

      <EquipmentTagDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((prev) => ({ ...prev, open }))}
        tag={dialog.tag}
        defaultParentId={dialog.parentId}
      />

      <AlertDialog
        open={tagToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setTagToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete „{tagToDelete?.name}“?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteStats?.descendantCount
                ? `Its ${deleteStats.descendantCount} sub-tag(s) are deleted as well. `
                : ""}
              {deleteStats?.equipmentCount
                ? `${deleteStats.equipmentCount} equipment item(s) lose this tag. `
                : ""}
              The equipment itself is not deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (tagToDelete) deleteTag.mutate(tagToDelete.id);
                setTagToDelete(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-equipment-tag"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EquipmentTagsPage;
